import React, { useState } from 'react';
import { CircleAlert, PencilLine, Plus, Search, Trash2, Zap } from 'lucide-react';
import { currentConnection } from 'src/features/Broadcasts/broadcastApi';
import { Skeleton, WsDialog } from 'src/stage/StageUi';
import quickReplyApi, { matchReplies, useQuickReplies } from './quickReplyApi';
import './quickReplies.css';

// AUTO-WA-39 Settings → Quick replies: the shared list of saved answers.
const STARTERS = [
  { shortcut: 'hours', title: 'Opening hours', body: 'We are open every day from 10 am to 8 pm.' },
  { shortcut: 'address', title: 'Our address', body: 'You can find us at <your address>. Here is the map: <map link>' },
  { shortcut: 'thanks', title: 'Thank you', body: 'Thank you for your order! We will share the delivery details shortly.' },
  { shortcut: 'payment', title: 'Payment options', body: 'You can pay by UPI, card or cash on delivery.' },
];

function ReplyForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState({ shortcut: initial?.shortcut || '', title: initial?.title || '', body: initial?.body || '' });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setErrors({});
    try { await onSave(form); onClose(); }
    catch (error) {
      const fields = error?.response?.data?.errors || {};
      setErrors(Object.keys(fields).length ? Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, value[0]])) : { form: error?.response?.data?.message || 'Couldn’t save. Try again.' });
      setBusy(false);
    }
  };
  return (
    <WsDialog title={initial?.id ? 'Edit quick reply' : 'New quick reply'} description="Your whole team can insert it in any chat." onClose={() => !busy && onClose()} wide>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="wa-qr-form-row">
          <div className="ws-field">
            <label htmlFor="qr-title">Name</label>
            <input id="qr-title" className="ws-input" value={form.title} onChange={set('title')} maxLength={80} placeholder="e.g. Store timings" required />
            {errors.title && <span className="wa-field-error">{errors.title}</span>}
          </div>
          <div className="ws-field">
            <label htmlFor="qr-shortcut">Shortcut</label>
            <input id="qr-shortcut" className="ws-input" value={form.shortcut} onChange={set('shortcut')} maxLength={40} placeholder="hours" required />
            {errors.shortcut ? <span className="wa-field-error">{errors.shortcut}</span> : <span className="wa-help">Search for it with /{form.shortcut || 'hours'}</span>}
          </div>
        </div>
        <div className="ws-field">
          <label htmlFor="qr-body">Message</label>
          <textarea id="qr-body" className="wa-textarea" value={form.body} onChange={set('body')} maxLength={1000} placeholder="The answer your team sends" required />
          {errors.body ? <span className="wa-field-error">{errors.body}</span> : <span className="wa-help">{form.body.length}/1000 · it is added to the message box to check before sending.</span>}
        </div>
        {errors.form && <div className="wa-notice wa-notice--bad" role="alert"><CircleAlert size={15} /><span>{errors.form}</span></div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="ws-btn ws-btn--quiet" data-close onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="ws-btn ws-btn--primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </WsDialog>
  );
}

export default function QuickRepliesSettings() {
  const workspaceId = currentConnection()?.workspaceId;
  const [state, reload] = useQuickReplies(workspaceId);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const items = matchReplies(state.items, query);

  const save = async (form) => {
    if (editing?.id) await quickReplyApi.update(workspaceId, editing.id, form);
    else await quickReplyApi.create(workspaceId, form);
    await reload();
  };
  const remove = async () => {
    setBusy(true);
    try { await quickReplyApi.remove(workspaceId, deleting.id); await reload(); setDeleting(null); }
    finally { setBusy(false); }
  };

  return (
    <div className="ws-set-stackcol">
      <section className="ws-card wa-set-card" aria-label="Quick replies">
        <div className="wa-card-head">
          <span className="ws-icon-tile" aria-hidden="true"><Zap size={18} /></span>
          <div className="ws-grow"><h2 className="ws-h3">Saved answers</h2><p className="ws-muted">In any chat, press the lightning button to insert one. Your team sees the same list.</p></div>
          {state.canManage && <button type="button" className="ws-btn ws-btn--primary ws-btn--sm" onClick={() => setEditing({})}><Plus size={14} />New quick reply</button>}
        </div>
        {state.items.length > 5 && (
          <div style={{ padding: '0 20px 12px' }}>
            <label className="ws-searchbox" style={{ maxWidth: 320 }}><Search size={15} aria-hidden="true" /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search quick replies" aria-label="Search quick replies" /></label>
          </div>
        )}
        {state.error && <div className="wa-notice wa-notice--bad" style={{ margin: '0 20px' }}><CircleAlert size={15} /><span>{state.error}</span></div>}
        <ul className="wa-list">
          {state.loading && [0, 1, 2].map((i) => <li key={i} style={{ padding: '8px 12px' }}><Skeleton height={44} /></li>)}
          {!state.loading && items.map((item) => (
            <li key={item.id} className="wa-list-item">
              <div className="wa-qr-row">
                <code className="wa-qr-code">/{item.shortcut}</code>
                <span className="ws-grow" style={{ minWidth: 0 }}>
                  <strong>{item.title}</strong>
                  <span className="ws-muted">{item.body}</span>
                </span>
                <span className="ws-muted wa-qr-uses">{item.use_count ? `Used ${item.use_count}×` : 'Not used yet'}</span>
                {state.canManage && (
                  <span className="wa-qr-actions">
                    <button type="button" className="ws-row-more" aria-label={`Edit ${item.title}`} onClick={() => setEditing(item)}><PencilLine size={14} /></button>
                    <button type="button" className="ws-row-more wa-danger-hover" aria-label={`Delete ${item.title}`} onClick={() => setDeleting(item)}><Trash2 size={14} /></button>
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
        {!state.loading && !state.error && state.items.length === 0 && (
          <div className="wa-qr-empty">
            <strong>Start with a few common answers</strong>
            <p className="ws-muted">Pick one to add it, then edit the words to fit your business.</p>
            <div className="wa-qr-starters">
              {STARTERS.map((starter) => (
                <button key={starter.shortcut} type="button" className="ws-link-tile" disabled={!state.canManage} onClick={() => setEditing(starter)}>
                  <Zap size={16} aria-hidden="true" /><span><strong>{starter.title}</strong><span className="ws-muted">{starter.body}</span></span>
                </button>
              ))}
            </div>
            {!state.canManage && <p className="ws-muted">Ask an owner, administrator or manager to add them.</p>}
          </div>
        )}
      </section>

      {editing && <ReplyForm initial={editing} onSave={save} onClose={() => setEditing(null)} />}
      {deleting && (
        <WsDialog title={`Delete “${deleting.title}”?`} description="It disappears from everyone’s quick replies. Messages already sent are not affected." onClose={() => !busy && setDeleting(null)}>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="ws-btn ws-btn--quiet" data-close onClick={() => setDeleting(null)} disabled={busy}>Keep it</button>
            <button type="button" className="ws-btn ws-btn--danger" onClick={remove} disabled={busy}><Trash2 size={14} />{busy ? 'Deleting…' : 'Delete'}</button>
          </div>
        </WsDialog>
      )}
    </div>
  );
}
